/**
 * NotificationBell — polls /api/notifications every 30s + updates live via SSE.
 * Shows unread badge + dropdown of unread @mentions.
 * Click a notification → navigates to the entity + marks it read.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, AtSign, Inbox, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useLive } from "../lib/live";
import { useAuth } from "../lib/auth";

const POLL_INTERVAL_MS = 30000;

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 30) return "à l'instant";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h`;
  const day = Math.floor(h / 24);
  if (day < 7) return `${day} j`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

function entityIcon(type) {
  if (type === "chelly_client") return "🗂️";
  if (type === "ticket" || type === "internal_ticket") return "🎫";
  return "📂";
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);
  const { subscribe } = useLive();
  const { user } = useAuth();

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { only_unread: false, limit: 25 } });
      setItems(data.items || []);
      setUnreadCount(data.unread_count || 0);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial + polling
  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [user, load]);

  // Live SSE — new mention for me
  useEffect(() => {
    return subscribe((ev) => {
      if (ev?.type !== "notification:new") return;
      if (ev?.payload?.user_email !== user?.email) return;
      setItems((prev) => [ev.payload.notification, ...prev].slice(0, 25));
      setUnreadCount((c) => c + 1);
    });
  }, [subscribe, user?.email]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id && !n.read_at ? { ...n, read_at: new Date().toISOString() } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await api.post(`/notifications/${id}/read`);
    } catch {
      // rollback silently
    }
  };

  const markAllRead = async () => {
    setUnreadCount(0);
    setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    try {
      await api.post("/notifications/read-all");
    } catch {}
  };

  const openNotification = async (n) => {
    if (!n.read_at) await markRead(n.id);
    setOpen(false);
    if (n.url_path) navigate(n.url_path);
  };

  if (!user) return null;

  return (
    <div className="relative" ref={rootRef} data-testid="notification-bell-root">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-1.5 text-norea-textMuted hover:text-norea-text hover:bg-norea-surfaceHover rounded transition"
        title="Notifications"
        data-testid="btn-notification-bell"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute left-full ml-2 bottom-0 w-[360px] bg-white border border-norea-border rounded-lg shadow-2xl z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-norea-border bg-norea-bg">
            <div className="text-xs font-semibold uppercase tracking-wider text-norea-textMuted">
              Notifications {unreadCount > 0 && <span className="ml-1 text-norea-primary">({unreadCount})</span>}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-[11px] text-norea-textMuted hover:text-norea-primary inline-flex items-center gap-1"
                data-testid="btn-mark-all-read"
              >
                <Check className="w-3 h-3" /> Tout marquer lu
              </button>
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="p-6 text-center text-norea-textMuted">
                <Loader2 className="w-4 h-4 animate-spin inline" />
              </div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-norea-textMuted text-xs">
                <Inbox className="w-6 h-6 mx-auto mb-2 opacity-40" />
                Aucune notification
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => openNotification(n)}
                  className={`w-full text-left px-3 py-2.5 border-b border-norea-border hover:bg-norea-surfaceHover transition ${
                    n.read_at ? "opacity-60" : "bg-norea-primary/[0.03]"
                  }`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-lg leading-none flex-shrink-0">{entityIcon(n.entity_type)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-1.5 flex-wrap">
                        <span className="text-xs font-semibold text-norea-text truncate">
                          <AtSign className="w-3 h-3 inline text-norea-primary" />
                          {n.author_name || n.author_email}
                        </span>
                        <span className="text-[10px] text-norea-textMuted">{timeAgo(n.created_at)}</span>
                      </div>
                      <div className="text-[11px] text-norea-textMuted mt-0.5 truncate">
                        sur <strong className="text-norea-text">{n.entity_label}</strong>
                      </div>
                      <div className="text-xs text-norea-text mt-1 line-clamp-2 whitespace-pre-wrap break-words">
                        {n.body_preview}
                      </div>
                    </div>
                    {!n.read_at && <span className="w-1.5 h-1.5 rounded-full bg-norea-primary mt-2 flex-shrink-0" />}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
